import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useIsScreenWidthBelow } from '@tebuto/functions/responsiveness'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import ExtraSmallText from '@tebuto/typography/ExtraSmallText'
import { Popover, PopoverContent, PopoverTrigger } from '@tebuto/widget/popover'
import { type ComponentPropsWithoutRef, forwardRef, useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTheme } from '../../../theme/ThemeProvider'
import { WidgetButton } from '../../../WidgetButton'
import CategoryFilters from './CategoryFilters'
import LocationFilters from './LocationFilters'
import TimeFilters from './TimeFilters'
import WeekdayFilters from './WeekdayFilters'

const FILTER_POPOVER_INSET_PX = 16

const CustomButton = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>(function CustomButton(props, ref) {
    const { t } = useComponentTranslation()
    return (
        <div ref={ref} {...props}>
            <WidgetButton className="widget-bg-field widget-border shadow-none" layout="outline">
                <div className="flex gap-2 items-center">
                    <FunnelIcon className="size-4 widget-text-secondary opacity-60" />
                    <ExtraSmallText className="widget-text-secondary">
                        {t('booking.bookingCalendar.adjustFilter')}
                    </ExtraSmallText>
                </div>
            </WidgetButton>
        </div>
    )
})

type TimeFiltersTheme = {
    backgroundColor: string
    textSecondary: string
    borderColor: string
    primaryColor: string
}

type FilterPanelProps = {
    timeFiltersTheme: TimeFiltersTheme
    onClose: () => void
    variant: 'desktop' | 'mobile'
}

function FilterPanel({ timeFiltersTheme, onClose, variant }: FilterPanelProps) {
    const { t } = useComponentTranslation()
    const titleId = useId()

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className={
                variant === 'desktop'
                    ? 'flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden rounded-xl border widget-border widget-bg-primary shadow-sm'
                    : 'flex h-full min-h-0 w-full flex-col overflow-hidden widget-bg-primary'
            }
            data-testid="booking-filter-panel"
        >
            <div className="shrink-0 border-b widget-border px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span id={titleId} className="font-semibold text-base widget-text-primary">
                            {t('booking.bookingCalendar.adjustFilter')}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md p-1 widget-text-secondary transition-colors hover:widget-bg-muted hover:widget-text-secondary outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-(--widget-primary) focus-visible:ring-offset-0"
                        data-testid="booking-filter-close"
                    >
                        <XMarkIcon className="size-5" />
                    </button>
                </div>
                <ExtraSmallText className="widget-text-secondary">
                    {t('booking.bookingCalendar.filterDescription')}
                </ExtraSmallText>
            </div>

            <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-6 popover-scrollable-content widget-scrollbar">
                <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
                    <div className="flex min-w-0 flex-col gap-6 overflow-visible">
                        <CategoryFilters />
                        <LocationFilters />
                    </div>
                    <div className="flex min-w-0 flex-col gap-6">
                        <TimeFilters theme={timeFiltersTheme} />
                        <WeekdayFilters />
                    </div>
                </div>
            </div>
        </div>
    )
}

function DesktopFilterOverlay({
    open,
    onClose,
    timeFiltersTheme
}: {
    open: boolean
    onClose: () => void
    timeFiltersTheme: TimeFiltersTheme
}) {
    const panelRef = useRef<HTMLDivElement>(null)
    const [widget, setWidget] = useState<HTMLElement | null>(null)

    useEffect(() => {
        setWidget(document.getElementById('tebuto-booking-widget'))
    }, [])

    useEffect(() => {
        if (!open) {
            return
        }

        function handleKeyDown(event: KeyboardEvent) {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        function handlePointerDown(event: MouseEvent) {
            const target = event.target
            if (!(target instanceof Node)) {
                return
            }
            if (panelRef.current?.contains(target)) {
                return
            }
            if (target instanceof Element && target.closest('[data-testid="booking-filter-trigger"]')) {
                return
            }
            onClose()
        }

        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('mousedown', handlePointerDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('mousedown', handlePointerDown)
        }
    }, [open, onClose])

    if (!open || !widget) {
        return null
    }

    return createPortal(
        <div
            ref={panelRef}
            className="absolute z-50"
            style={{
                top: FILTER_POPOVER_INSET_PX,
                right: FILTER_POPOVER_INSET_PX,
                bottom: FILTER_POPOVER_INSET_PX,
                left: FILTER_POPOVER_INSET_PX
            }}
        >
            <FilterPanel timeFiltersTheme={timeFiltersTheme} onClose={onClose} variant="desktop" />
        </div>,
        widget
    )
}

export default function FilterArea() {
    const { theme } = useTheme()
    const isBelowLg = useIsScreenWidthBelow(1024)
    const [open, setOpen] = useState(false)

    const timeFiltersTheme: TimeFiltersTheme = {
        backgroundColor: theme.backgroundColor,
        textSecondary: theme.textSecondary,
        borderColor: theme.borderColor ?? '#d1d5db',
        primaryColor: theme.primaryColor
    }

    const close = useCallback(() => setOpen(false), [])

    if (isBelowLg) {
        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <CustomButton data-testid="booking-filter-trigger" />
                </PopoverTrigger>
                <PopoverContent
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    className="widget-bg-primary z-50 p-0"
                >
                    <FilterPanel timeFiltersTheme={timeFiltersTheme} onClose={close} variant="mobile" />
                </PopoverContent>
            </Popover>
        )
    }

    return (
        <>
            <CustomButton data-testid="booking-filter-trigger" onClick={() => setOpen((current) => !current)} />
            <DesktopFilterOverlay open={open} onClose={close} timeFiltersTheme={timeFiltersTheme} />
        </>
    )
}
